import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Badge } from "../../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { ShieldOff, ShieldCheck } from "lucide-react";
import { MKT, getAdminAuth } from "./AdminEmailMarketing";

const STATUS_COLORS = {
  unsubscribed: "bg-amber-500/15 text-amber-500",
  hard_bounce: "bg-red-500/15 text-red-400",
  spam_complaint: "bg-red-600/20 text-red-500",
  manual: "bg-slate-500/15 text-slate-400",
  invalid: "bg-orange-500/15 text-orange-400",
};

export default function SuppressionsTab({ onChanged }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [newEmail, setNewEmail] = useState("");

  const fetchItems = async () => {
    try {
      const params = new URLSearchParams({ search });
      if (status !== "all") params.set("status", status);
      const res = await axios.get(`${MKT}/suppressions?${params}`, getAdminAuth());
      setItems(res.data.suppressions);
      setTotal(res.data.total);
    } catch (e) { toast.error("Failed to load suppressions"); }
  };
  useEffect(() => { fetchItems(); }, [search, status]); // eslint-disable-line

  const addSuppression = async () => {
    try {
      await axios.post(`${MKT}/suppressions`, { email: newEmail, reason: "manually added" }, getAdminAuth());
      toast.success("Suppressed");
      setNewEmail(""); fetchItems(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (email) => {
    if (!window.confirm(`Remove ${email} from the suppression list? They will become eligible for marketing emails again.`)) return;
    try {
      await axios.delete(`${MKT}/suppressions/${encodeURIComponent(email)}`, getAdminAuth());
      toast.success("Removed from suppression");
      fetchItems(); onChanged?.();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4 mt-4" data-testid="marketing-suppressions-tab">
      <p className="text-xs text-muted-foreground">
        Suppressed addresses NEVER receive marketing campaigns — regardless of lists, re-imports or queued campaigns.
        Transactional emails (invoices, password resets…) are unaffected.
      </p>
      <div className="flex flex-wrap gap-2 items-center">
        <Input placeholder="Search email…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs" data-testid="suppression-search" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-44" data-testid="suppression-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.keys(STATUS_COLORS).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Input placeholder="email to suppress" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className="max-w-xs" data-testid="new-suppression-email" />
        <Button size="sm" onClick={addSuppression} disabled={!newEmail.trim()} data-testid="add-suppression-btn">
          <ShieldOff className="w-4 h-4 mr-1" />Suppress
        </Button>
      </div>
      <Card className="border-border">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="suppressions-table">
            <thead><tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
              <th className="p-3">Email</th><th className="p-3">Status</th><th className="p-3">Source</th>
              <th className="p-3">Campaign</th><th className="p-3">Updated</th><th className="p-3 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {items.map(s => (
                <tr key={s.email} className="border-b border-border/40">
                  <td className="p-3 font-medium text-foreground">{s.email}</td>
                  <td className="p-3"><Badge className={`text-[10px] ${STATUS_COLORS[s.status] || ""}`}>{s.status}</Badge></td>
                  <td className="p-3 text-xs text-muted-foreground">{s.source || "—"}</td>
                  <td className="p-3 text-xs font-mono text-muted-foreground">{s.campaign_id || "—"}</td>
                  <td className="p-3 text-xs text-muted-foreground">{s.updated_at ? new Date(s.updated_at).toLocaleString() : "—"}</td>
                  <td className="p-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => remove(s.email)} title="Remove suppression (intentional)" data-testid={`unsuppress-${s.email}`}>
                      <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                    </Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground text-sm">No suppressions</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
      <p className="text-xs text-muted-foreground">{total} suppressed addresses</p>
    </div>
  );
}
